import { getByID } from '@adornis/baseql/operations/mongo';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog';
import { useState } from '@adornis/functional-lit/stateful';
import { useQuery } from '@adornis/functional-lit/use-query';
import { webcomponent } from '@adornis/functional-lit/webcomponent';
import { Size } from '@adornis/html-based-buildify/db/enums';
import { html, nothing } from 'lit';
import { getBundleFilesByReferenceID } from '../api/getBundleFilesByReferenceID';
import { BundleFileMeta, LASFile } from '../db/LASFile';
import { FileUploadArea } from './FileUploadArea';
import type { SupportedFileType } from './las-file-explorer';

type UploadedFile = { size: Size; file: File };

export const MediaBundleDialog = async ({
  fileReferenceID,
  allowedFileTypes,
}: {
  fileReferenceID: string;
  allowedFileTypes: SupportedFileType[];
}) => {
  return XNativeDialog.showPopup<UploadedFile[]>({
    content: (resolve, reject, dialogElement) => {
      return MediaBundleContent.render({
        fileReferenceID,
        allowedFileTypes,
        onSave: resolve,
        onCancel: reject,
      });
    },
  });
};

export const MediaBundleContent = webcomponent(
  'media-bundle-content',
  ({
    fileReferenceID,
    allowedFileTypes,
    onSave,
    onCancel,
  }: {
    fileReferenceID: string;
    allowedFileTypes: SupportedFileType[];
    onSave: (uploadedFiles: UploadedFile[]) => void;
    onCancel: () => void;
  }) => {
    const { value: fileReference, loading: isFileReferenceLoading } = useQuery(
      () => getByID(LASFile, fileReferenceID)({ _id: 1 }),
      [fileReferenceID],
    );
    if (isFileReferenceLoading || !fileReference) return nothing;

    const { value: bundleFiles, loading: isBundleFilesLoading } = useQuery(
      () =>
        getBundleFilesByReferenceID(fileReferenceID)({
          _id: 1,
          createdAt: 1,
          meta: {
            _class: 1,
            size: 1,
            mimetype: 1,
            md5: 1,
            fileName: 1,
            extension: 1,
            completeName: 1,
          },
          altTag: 1,
          bundleMeta: {
            _class: 1,
            size: 1,
            fileReferenceID: 1,
          },
        }),
      [fileReferenceID],
    );
    if (isBundleFilesLoading || !bundleFiles) return nothing;

    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [filesToReplace, setFilesToReplace] = useState<Size[]>([]);

    const sizeFileHandler = (size: Size) => {
      const matchingFile = bundleFiles.find(file => file.bundleMeta?.size === size);
      const uploadedFile = uploadedFiles.find(file => file.size === size);
      const isReplacing = filesToReplace.includes(size);

      // Übersetzungskonstanten für bessere Lesbarkeit
      const sizeLabels: Record<Size, string> = {
        [Size.MOBILE]: 'Mobil',
        [Size.TABLET]: 'Tablet',
        [Size.DESKTOP]: 'Desktop',
      };

      // Wenn eine Datei ersetzt werden soll oder keine Datei vorhanden ist und auch keine hochgeladen wurde
      if (isReplacing || (!matchingFile && !uploadedFile)) {
        return FileUploadArea({
          allowedFileTypes,
          onValuePicked: async files => {
            const file = files[0];
            if (!file) return;

            // Neue Datei hochladen und aus der Liste der zu ersetzenden Dateien entfernen
            setUploadedFiles([...uploadedFiles.filter(f => f.size !== size), { size, file }]);
            setFilesToReplace(filesToReplace.filter(s => s !== size));
          },
          title: isReplacing ? `${sizeLabels[size]} ersetzen` : sizeLabels[size],
          acceptMultipleFiles: false,
        });
      }

      // if there is a matching file and no uploaded file, show the file
      if (matchingFile && !uploadedFile)
        return html`
          <div class="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-300">
            <div class="card-body p-4">
              <div class="flex items-center justify-between">
                <div class="badge badge-accent badge-outline">${sizeLabels[size]}</div>
                <div class="opacity-70 text-xs">${formatFileSize(matchingFile.meta?.size ?? 0)}</div>
              </div>

              <div class="my-2">
                <p class="text-sm font-medium truncate">${matchingFile.meta?.fileName}</p>
                <p class="text-xs opacity-60">${formatDate(matchingFile.createdAt)}</p>
              </div>

              ${(() => {
                const fileUrl = LASFile.getServeLink(matchingFile._id);
                const fileExtension = matchingFile.meta?.extension?.toLowerCase() || '';

                if (isImageFile(`${matchingFile.meta?.fileName}.${matchingFile.meta?.extension}` || '')) {
                  return html`
                    <div class="flex justify-center my-3">
                      <div
                        class="relative overflow-hidden rounded-lg bg-base-200/30 w-full h-36 flex items-center justify-center"
                      >
                        <img
                          src=${fileUrl}
                          alt="${matchingFile.meta?.fileName || 'Datevorschau'}"
                          class="object-contain max-h-36 max-w-full shadow-sm"
                        />
                      </div>
                    </div>
                  `;
                } else if (fileExtension === 'pdf') {
                  return html`
                    <div class="flex justify-center my-3">
                      <div
                        class="relative overflow-hidden rounded-lg bg-base-200/30 w-full h-36 flex flex-col items-center justify-center"
                      >
                        <span class="text-4xl text-accent mb-2">
                          <i class="bi bi-file-earmark-pdf"></i>
                        </span>
                        <div class="badge badge-sm badge-primary">PDF</div>
                        <a href=${fileUrl} target="_blank" class="btn btn-xs btn-ghost mt-2">
                          <span class="text-xs">Öffnen</span>
                        </a>
                      </div>
                    </div>
                  `;
                } else {
                  return html`
                    <div class="flex justify-center my-3">
                      <div
                        class="relative overflow-hidden rounded-lg bg-base-200/30 w-full h-36 flex flex-col items-center justify-center"
                      >
                        <span class="text-4xl text-accent mb-2">
                          <i class="bi bi-${getFileTypeIcon(matchingFile.meta?.fileName || '')}"></i>
                        </span>
                        <div class="badge badge-sm badge-neutral font-mono">${fileExtension.toUpperCase()}</div>
                      </div>
                    </div>
                  `;
                }
              })()}

              <div class="card-actions justify-end">
                <button
                  class="btn btn-sm btn-ghost"
                  @click=${() => {
                    // Zu der Liste der zu ersetzenden Dateien hinzufügen
                    setFilesToReplace([...filesToReplace, size]);
                  }}
                >
                  <span class="text-xs">Ersetzen</span>
                </button>
              </div>
            </div>
          </div>
        `;

      if (uploadedFile) {
        return html`
          <div class="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-300">
            <div class="card-body p-4">
              <div class="flex items-center justify-between">
                <div class="flex gap-1.5">
                  <div class="badge badge-secondary badge-sm">Neu</div>
                  <div class="badge badge-accent badge-outline badge-sm">${sizeLabels[size]}</div>
                </div>
                <div class="opacity-70 text-xs">${formatFileSize(uploadedFile.file.size)}</div>
              </div>

              <p class="text-sm font-medium truncate mt-2">${uploadedFile.file.name}</p>

              ${isImageFile(uploadedFile.file.name)
                ? html`
                    <div class="flex justify-center my-3">
                      <div
                        class="relative overflow-hidden rounded-lg bg-base-200/30 w-full h-36 flex items-center justify-center"
                      >
                        <img
                          src=${URL.createObjectURL(uploadedFile.file)}
                          alt="Vorschau"
                          class="object-contain max-h-36 max-w-full shadow-sm"
                        />
                      </div>
                    </div>
                  `
                : html`
                    <div class="flex justify-center my-3">
                      <div
                        class="relative overflow-hidden rounded-lg bg-base-200/30 w-full h-36 flex flex-col items-center justify-center"
                      >
                        <span class="text-4xl text-accent mb-2">
                          <i class="bi bi-${getFileTypeIcon(uploadedFile.file.name)}"></i>
                        </span>
                        <div class="badge badge-sm badge-neutral font-mono">
                          ${uploadedFile.file.name.split('.').pop()?.toUpperCase()}
                        </div>
                      </div>
                    </div>
                  `}

              <div class="card-actions justify-end mt-1">
                <button
                  class="btn btn-sm btn-ghost text-error"
                  @click=${() => {
                    setUploadedFiles(uploadedFiles.filter(file => file.size !== size));
                  }}
                >
                  <span class="text-xs">Entfernen</span>
                </button>
              </div>
            </div>
          </div>
        `;
      }

      return html`
        <div class="card bg-base-100 shadow-sm border border-base-200">
          <div class="card-body p-4">
            <div class="flex justify-between items-center mb-2">
              <div class="badge badge-ghost badge-outline">${sizeLabels[size]}</div>
            </div>

            <div class="flex justify-center items-center h-36 bg-base-200/30 rounded-lg">
              <span class="text-base-content/40 text-sm">Keine Datei vorhanden</span>
            </div>

            <div class="card-actions justify-center mt-3">
              <button class="btn btn-sm btn-ghost btn-outline" @click=${() => {}}>
                <span class="text-xs">Datei hinzufügen</span>
              </button>
            </div>
          </div>
        </div>
      `;
    };

    return html`
      <div class="card w-full max-w-3xl bg-base-100 shadow-xl">
        <div class="card-body p-6">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold">Media Bundle</h2>
            <div class="text-xs opacity-70">Verschiedene Größen für unterschiedliche Geräte</div>
          </div>

          <div class="divider my-2"></div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-5 my-2">
            ${sizeFileHandler(Size.MOBILE)} ${sizeFileHandler(Size.TABLET)} ${sizeFileHandler(Size.DESKTOP)}
          </div>

          <div class="divider my-2"></div>

          <div class="card-actions justify-between mt-2">
            <button class="btn btn-ghost" @click=${onCancel}>Abbrechen</button>
            <button
              class="btn btn-accent"
              @click=${async () => {
                const performUpload = async () => {
                  for (const file of uploadedFiles) {
                    const existingFile = bundleFiles.find(f => f.bundleMeta?.size === file.size);
                    setFilesToReplace(filesToReplace.filter(s => s !== file.size));

                    const uploadedFileID = existingFile
                      ? await existingFile.upload(file.file)
                      : await LASFile.upload(file.file);

                    const uploadedFile = await getByID(
                      LASFile,
                      uploadedFileID,
                    )({
                      _id: 1,
                      bundleMeta: {
                        _class: 1,
                        size: 1,
                        fileReferenceID: 1,
                      },
                    });
                    if (!uploadedFile) continue;

                    uploadedFile.bundleMeta = new BundleFileMeta({
                      size: file.size,
                      fileReferenceID,
                    });

                    await uploadedFile.save();
                  }

                  // remove files that have been replaced
                  for (const size of filesToReplace) {
                    const file = bundleFiles.find(f => f.bundleMeta?.size === size);
                    if (file) {
                      file.bundleMeta = null;
                      await file.save();
                    }
                  }
                };

                await XNativeDialog.waitFor(performUpload(), 'Dateien werden hochgeladen...').then(() => {
                  XSnackbar.show('Media Bundle gespeichert!');
                  onSave(uploadedFiles);
                });
              }}
            >
              Speichern
            </button>
          </div>
        </div>
      </div>
    `;
  },
  {
    shadowRoot: true,
  },
);

// Hilfsfunktionen für optimierte Anzeige
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (date?: Date): string => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const isImageFile = (file: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const fileExtension = file.split('.').pop()?.toLowerCase();

  if (file.startsWith('data:image/')) {
    return true;
  }

  return imageExtensions.includes(fileExtension || '');
};

const getFileTypeIcon = (file: string): string => {
  const extension = file.split('.').pop()?.toLowerCase() || '';

  const iconMap: Record<string, string> = {
    pdf: 'file-earmark-pdf',
    doc: 'file-earmark-word',
    docx: 'file-earmark-word',
    xls: 'file-earmark-excel',
    xlsx: 'file-earmark-excel',
    txt: 'file-earmark-text',
    zip: 'file-earmark-zip',
    rar: 'file-earmark-zip',
    mp3: 'file-earmark-music',
    mp4: 'file-earmark-play',
    ppt: 'file-earmark-slides',
    pptx: 'file-earmark-slides',
  };

  return iconMap[extension] || 'file-earmark';
};
